import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://monitoring.leetcode.uz/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Dashboard API
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getActivityTimeline = (hours = 24, workerId = null) => {
  const params = new URLSearchParams();
  params.append('hours', hours);
  if (workerId) params.append('worker_id', workerId);
  return api.get(`/dashboard/activity?${params.toString()}`);
};
export const getRiskSummary = (workerId = null) => {
  const params = workerId ? `?worker_id=${workerId}` : '';
  return api.get(`/dashboard/risk-summary${params}`);
};

// Screenshots API
export const getScreenshots = (limit = 200, offset = 0, workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('offset', offset);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/screenshots/list?${params.toString()}`);
};
export const getScreenshotImage = (id) => 
  `${API_BASE_URL}/screenshots/image/${id}`;
export const getScreenshotDetails = (id) => 
  api.get(`/screenshots/details/${id}`);

// Applications API
export const getAppUsage = (hours = null, workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (hours) params.append('hours', hours);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/applications/usage?${params.toString()}`);
};
export const getApplicationTimeline = (hours = null, workerId = null, startDate = null, endDate = null, limit = 200) => {
  const params = new URLSearchParams();
  if (hours) params.append('hours', hours);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('limit', limit);
  return api.get(`/applications/timeline?${params.toString()}`);
};

/** Horizontal timeline + AFK overlays (worker required). */
export const getTimelineChart = ({ hours = null, workerId, startDate = null, endDate = null, limit = 1500 }) => {
  const params = new URLSearchParams();
  params.append('worker_id', workerId);
  if (hours) params.append('hours', hours);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  params.append('limit', limit);
  return api.get(`/applications/timeline-chart?${params.toString()}`);
};

export const createAfkPeriod = ({ worker_id, start_time, end_time, note }) =>
  api.post('/applications/afk', { worker_id, start_time, end_time, note });

export const deleteAfkPeriod = (periodId) => api.delete(`/applications/afk/${periodId}`);

export const getAppCategories = () => 
  api.get('/applications/categories');

// Clipboard API
export const getClipboardEvents = (limit = 200, workerId = null, startDate = null, endDate = null, contentType = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (contentType) params.append('content_type', contentType);
  return api.get(`/clipboard/list?${params.toString()}`);
};
export const getClipboardEventDetails = (id) =>
  api.get(`/clipboard/details/${id}`);
export const getClipboardRiskBreakdown = (id) =>
  api.get(`/clipboard/risk/${id}`);
export const getClipboardImage = (id) =>
  `${API_BASE_URL}/clipboard/image/${id}`;

// USB API
export const getUSBEvents = (limit = 50, workerId = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (workerId) params.append('worker_id', workerId);
  return api.get(`/usb/list?${params.toString()}`);
};
export const getUSBEventDetails = (id) =>
  api.get(`/usb/details/${id}`);

// Incidents API
export const getIncidents = (severity = null, limit = 50, workerId = null) => {
  const params = new URLSearchParams();
  if (severity) params.append('severity', severity);
  params.append('limit', limit);
  if (workerId) params.append('worker_id', workerId);
  return api.get(`/incidents/list?${params.toString()}`);
};
export const getIncidentDetails = (id) => 
  api.get(`/incidents/details/${id}`);

// Settings API
export const getConfiguration = () => 
  api.get('/settings/config');
export const updateConfiguration = (key, value) => 
  api.post('/settings/config', { key, value });
export const getMonitoringStatus = () => 
  api.get('/settings/monitoring-status');

// Browser API
export const getBrowserHistory = (hours = null, limit = 500, workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (hours) params.append('hours', hours);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/browser/history?${params.toString()}`);
};
export const getBrowserCategories = (workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/browser/categories?${params.toString()}`);
};
export const getBrowserAnalytics = (workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/browser/analytics?${params.toString()}`);
};

// Printer API
export const getPrintJobs = (limit = 100) =>
  api.get(`/printer/jobs?limit=${limit}`);
export const getPrinterSummary = () =>
  api.get('/printer/summary');

// Network API
export const getNetworkActivity = (hours = null, limit = 500, workerId = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (hours) params.append('hours', hours);
  if (workerId) params.append('worker_id', workerId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/network/activity?${params.toString()}`);
};
export const getNetworkSummary = () =>
  api.get('/network/summary');

// Analytics API
export const getBehaviorPatterns = (hours = 24) =>
  api.get(`/analytics/patterns?hours=${hours}`);
export const getProductivityMetrics = (hours = 24) =>
  api.get(`/analytics/productivity?hours=${hours}`);
export const getRiskTrends = (days = 7) =>
  api.get(`/analytics/trends?days=${days}`);

// Workers API
export const getWorkers = (status = null) => {
  const params = status ? `?status=${status}` : '';
  return api.get(`/workers/list${params}`).then(res => res.data);
};
export const getPendingWorkers = () =>
  api.get('/workers/pending').then(res => res.data);
export const getWorkerDetails = (workerId) =>
  api.get(`/workers/${workerId}`).then(res => res.data);
export const approveWorker = (workerId, approve) =>
  api.post('/workers/approve', { worker_id: workerId, approve }).then(res => res.data);
export const deleteWorker = (workerId) =>
  api.delete(`/workers/${workerId}`).then(res => res.data);

// Updates API
export const getLatestVersion = () =>
  api.get('/updates/latest').then(res => res.data);

// Health check
export const checkHealth = () => 
  axios.get(`${API_BASE_URL.replace('/api', '')}/api/health`);

// Policies API
export const getPolicies = () => 
  api.get('/policies/list');
export const getPolicy = (policyName) => 
  api.get(`/policies/${policyName}`);
export const savePolicy = (policy) => 
  api.post('/policies/save', policy);
export const deletePolicy = (policyName) => 
  api.delete(`/policies/${policyName}`);

// Control API
export const terminateSession = (workerId, sessionId = null) => 
  api.post('/control/terminate-session', { worker_id: workerId, session_id: sessionId });
export const pushPolicy = (workerId, policy) => 
  api.post('/control/push-policy', { worker_id: workerId, policy });
export const updateWorkerConfig = (workerId, config) => 
  api.post('/control/update-config', { worker_id: workerId, config });
export const enableMonitoring = (workerId, monitorTypes) => 
  api.post('/control/enable-monitoring', { worker_id: workerId, monitor_types: monitorTypes });
export const disableMonitoring = (workerId, monitorTypes) => 
  api.post('/control/disable-monitoring', { worker_id: workerId, monitor_types: monitorTypes });
export const getPendingCommands = (workerId) => 
  api.get(`/control/pending-commands/${workerId}`);

// WebSocket connection helper
export const createWebSocket = (onMessage, onError, onClose) => {
  const wsUrl = (process.env.REACT_APP_WS_URL || 'ws://127.0.0.1:8765').replace('http://', 'ws://').replace('https://', 'wss://');
  const ws = new WebSocket(`${wsUrl}/ws`);
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e);
    }
  };
  
  ws.onerror = onError;
  ws.onclose = onClose;
  
  return ws;
};

export default api;

